import { Controller } from "@hotwired/stimulus";
import * as monaco from "monaco-editor";
import * as lz from "lz-string";

// Configure Monaco Editor workers for Vite
self.MonacoEnvironment = {
  getWorkerUrl: function (moduleId, label) {
    return new URL(
      "monaco-editor/esm/vs/editor/editor.worker.js",
      import.meta.url,
    ).href;
  },
};

// Define the Tomorrow Night theme
monaco.editor.defineTheme("tomorrow-night", {
  base: "vs-dark",
  inherit: true,
  rules: [
    { background: "1D1F21", token: "" },
    { foreground: "969896", token: "comment" },
    { foreground: "cc6666", token: "variable" },
    { foreground: "cc6666", token: "string.regexp" },
    { foreground: "cc6666", token: "entity.name.tag" },
    { foreground: "cc6666", token: "entity.other.attribute-name" },
    { foreground: "de935f", token: "constant.numeric" },
    { foreground: "de935f", token: "constant.language" },
    { foreground: "de935f", token: "variable.parameter" },
    { foreground: "f0c674", token: "entity.name.class" },
    { foreground: "f0c674", token: "entity.name.type.class" },
    { foreground: "f0c674", token: "support.type" },
    { foreground: "b5bd68", token: "string" },
    { foreground: "b5bd68", token: "constant.other.symbol" },
    { foreground: "8abeb7", token: "keyword.operator" },
    { foreground: "81a2be", token: "entity.name.function" },
    { foreground: "81a2be", token: "support.function" },
    { foreground: "b294bb", token: "keyword" },
    { foreground: "b294bb", token: "storage" },
    { foreground: "b294bb", token: "storage.type" },
  ],
  colors: {
    "editor.foreground": "#C5C8C6",
    "editor.background": "#1D1F21",
    "editor.selectionBackground": "#373B41",
    "editor.lineHighlightBackground": "#282A2E",
    "editorCursor.foreground": "#AEAFAD",
    "editorWhitespace.foreground": "#4B4E55",
  },
});

const defaultSettings = {
  language: "ruby",
  theme: "tomorrow-night",
  automaticLayout: true,
  fontSize: 14,
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  lineNumbers: "on",
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  renderLineHighlight: "all",
  tabSize: 2,
  insertSpaces: true,
};

export default class MonacoController extends Controller {
  static targets = ["editor", "input"];
  static values = {
    target: { type: String, default: "input" }, // input | output
  };

  connect() {
    this.#initializeEditor();

    if (this.#isInput) {
      this.element.closest("form").requestSubmit();
    }
  }

  disconnect() {
    if (this.editor) {
      this.editor.dispose();
    }
  }

  get inputValue() {
    return this.inputTarget.value;
  }

  // private

  #initializeEditor() {
    this.editor = monaco.editor.create(this.editorTarget, {
      ...defaultSettings,
      value: this.#initialEditorValue,
      readOnly: this.#isOutput,
      domReadOnly: this.#isOutput,
      contextmenu: this.#isInput,
    });

    if (this.#isInput) {
      this.inputTarget.value = this.#initialEditorValue;

      this.editor.onDidChangeModelContent(() => {
        this.inputTarget.value = this.editor.getValue();
        this.#updateURL();
      });
    }
  }

  #updateURL() {
    window.location.hash = this.#compressedValue;
  }

  get #hasHashValue() {
    return !!window.location.hash;
  }

  get #initialEditorValue() {
    return this.#hasHashValue && this.#isInput
      ? this.#decompressedValue
      : this.inputValue;
  }

  get #compressedValue() {
    return lz.compressToEncodedURIComponent(this.inputTarget.value);
  }

  get #decompressedValue() {
    return lz.decompressFromEncodedURIComponent(window.location.hash.slice(1));
  }

  get #isInput() {
    return this.targetValue === "input";
  }

  get #isOutput() {
    return this.targetValue === "output";
  }
}
